import { type CSSProperties } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import classNames from 'classnames'

import { type ExportAttributeDefinition } from './types'
import { Icon } from '@/Atomic/Atoms/Icon'

interface SortableSelectedAttributeItemProps {
  attribute: ExportAttributeDefinition
  canMoveDown: boolean
  canMoveUp: boolean
  dragLabel: string
  fixedColumnsLabel: string
  index: number
  isInvalidDropTarget: boolean
  isPinned: boolean
  onMove: (attributeKey: string, direction: -1 | 1) => void
  onRemove: (attributeKey: string) => void
  pinnedLabel: string
}

export const SortableSelectedAttributeItem: React.FC<
  SortableSelectedAttributeItemProps
> = ({
  attribute,
  canMoveDown,
  canMoveUp,
  dragLabel,
  fixedColumnsLabel,
  index,
  isInvalidDropTarget,
  isPinned,
  onMove,
  onRemove,
  pinnedLabel,
}) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: attribute.key,
    disabled: isPinned,
  })

  const style: CSSProperties = {
    opacity: isDragging ? 0.45 : undefined,
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const dragHandleProps = isPinned ? {} : { ...attributes, ...listeners }

  return (
    <div
      className={classNames('export-csv-modal__selected-item', {
        'is-fixed': isPinned,
        'is-dragging': isDragging,
        'is-invalid-drop': isInvalidDropTarget,
      })}
      data-export-attribute-key={attribute.key}
      ref={setNodeRef}
      style={style}
    >
      <div className="export-csv-modal__selected-index">{index + 1}</div>
      <button
        type="button"
        className="export-csv-modal__selected-drag"
        aria-label={dragLabel}
        disabled={isPinned}
        {...dragHandleProps}
      >
        <Icon icon="drag-drop-vertical" size="sm" />
      </button>
      <div className="export-csv-modal__selected-copy">
        <div className="export-csv-modal__selected-topline">
          <span className="export-csv-modal__selected-label wizard-modal-label">
            {attribute.label}
          </span>
          {isPinned && (
            <span className="export-csv-modal__selected-badge">
              {pinnedLabel}
            </span>
          )}
        </div>
        <div className="export-csv-modal__selected-group wizard-modal-meta">
          {isPinned ? fixedColumnsLabel : attribute.groupTitle}
        </div>
      </div>
      <div className="export-csv-modal__selected-actions">
        <button
          type="button"
          className="export-csv-modal__icon-button"
          onClick={() => onMove(attribute.key, -1)}
          disabled={!canMoveUp}
        >
          <Icon icon="chevron-up" size="sm" />
        </button>
        <button
          type="button"
          className="export-csv-modal__icon-button"
          onClick={() => onMove(attribute.key, 1)}
          disabled={!canMoveDown}
        >
          <Icon icon="chevron-down" size="sm" />
        </button>
        <button
          type="button"
          className="export-csv-modal__icon-button"
          onClick={() => onRemove(attribute.key)}
          disabled={isPinned}
        >
          <Icon icon="cross" size="sm" />
        </button>
      </div>
    </div>
  )
}
